import { ApiError, ErrorType, FormErrors } from '../models/ApiError';
import { defaultErrorHandler } from '../services/ErrorHandler';

/**
 * Handles form-specific error extraction and mapping
 * 
 * @deprecated Use ErrorHandler class instead for better reusability
 * This class is kept for backward compatibility
 */
export class FormErrorHandler {
  /**
   * Extract field-specific errors from an ApiError
   * 
   * @deprecated Use defaultErrorHandler.extractFieldErrors() instead
   */
  static extractFieldErrors(error: ApiError): FormErrors {
    return defaultErrorHandler.extractFieldErrors(error);
  }

  /**
   * Get a specific error message for a field
   */
  private static getFieldErrorMessage(field: string, value: any, details: any): string {
    const fieldName = this.formatFieldName(field);

    // Check for expected format in details
    if (details.expectedFormat) {
      return `Invalid ${fieldName}. Expected format: ${details.expectedFormat}`;
    }

    // Field-specific messages
    switch (field) {
      case 'email':
        return 'Please enter a valid email address';
      case 'phone_number':
      case 'phoneNumber':
        return 'Please enter a valid phone number';
      case 'password':
        return 'Password does not meet requirements';
      case 'username':
        return 'Invalid username';
      case 'orderId':
        return 'Invalid order ID';
      case 'clientInnerId':
        return 'Invalid client ID';
      default:
        return `Invalid ${fieldName}`;
    }
  }

  /**
   * Format field name for display (convert snake_case or camelCase to Title Case)
   */
  private static formatFieldName(field: string): string {
    return field
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .toLowerCase()
      .replace(/^\w/, (c) => c.toUpperCase())
      .trim();
  }

  /**
   * Check if an error contains form validation errors
   * 
   * @deprecated Use defaultErrorHandler.hasFormErrors() instead
   */
  static hasFormErrors(error: ApiError): boolean {
    return defaultErrorHandler.hasFormErrors(error);
  }

  /**
   * Get a general form error message (non-field-specific)
   * 
   * @deprecated Use defaultErrorHandler.getGeneralFormError() instead
   */
  static getGeneralFormError(error: ApiError): string | null {
    return defaultErrorHandler.getGeneralFormError(error);
  }

  /**
   * Map backend field names to frontend form field names if they differ
   * 
   * @deprecated Configure field mapping in ErrorHandler instead
   */
  static mapBackendFieldToFrontend(backendField: string): string {
    const fieldMap: Record<string, string> = {
      'custom:role': 'role',
      'custom:app': 'app',
      userName: 'username',
      // Add more mappings as needed
    };

    return fieldMap[backendField] || backendField;
  }

  /**
   * Convert form errors to a format suitable for display in a list
   * 
   * @deprecated Use defaultErrorHandler.formErrorsToList() instead
   */
  static formErrorsToList(errors: FormErrors): string[] {
    return defaultErrorHandler.formErrorsToList(errors);
  }
}

